import React, { useEffect, useState } from "react";
import moment from "moment-timezone";
import {
  getNextPrayerOrJummah,
  getOverlayTypeForCurrentTime,
  getSecondsUntil,
} from "../../utils/prayerUtils";

import CountdownOverlay from "./CountdownOverlay";
import IqamahOverlay from "./IqamahOverlay";
import SalahProgressOverlay from "./SalahProgressOverlay";

const PrayerOverlayController = ({
  iqamahTimes,
  jummahTimes,
  timezone,
  onOverlayChange,
}) => {
  const [now, setNow] = useState(moment.tz(moment(), timezone || moment.tz.guess()));
  const [nextEvent, setNextEvent] = useState(null);
  const [overlayType, setOverlayType] = useState(null);
  const [countdownSeconds, setCountdownSeconds] = useState(0);

  //console.log("🚀 PrayerOverlayController mounted");

  // console.log("Lala: timezone =", timezone, iqamahTimes, jummahTimes, onOverlayChange);
  // 🕒 Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(moment.tz(moment(), timezone || moment.tz.guess()));
    }, 1000);
    return () => clearInterval(timer);
  }, [timezone]);

  // 🧩 2. Determine next prayer/jummah event
  useEffect(() => {
    // If iqamahTimes hasn't arrived yet, just skip (no warning)
    if (iqamahTimes == null) return;

    // Only warn when it's clearly wrong (like empty array or non-array)
    if (!iqamahTimes || Object.keys(iqamahTimes).length === 0) {
      console.warn("⚠️ iqamahTimes is empty or invalid:", iqamahTimes);
      setNextEvent(null);
      return;
    }

    //console.log("🕋 [OverlayController] Current Time:", now.format("hh:mm:ss A"));

    try {
      const event = getNextPrayerOrJummah(now, iqamahTimes, jummahTimes, 20);
      setNextEvent(event);
      //console.log("🕌 [OverlayController] Next Event:", event);
    } catch (err) {
      console.error("❌ Error in getNextPrayerOrJummah:", err);
      setNextEvent(null);
    }
  }, [now, iqamahTimes, jummahTimes]);


  // 🧩 3. Determine which overlay should be shown
  useEffect(() => {
    if (!nextEvent) {
      if (overlayType !== null) {
        //console.log("❌ [OverlayController] No nextEvent, clearing overlay");
        setOverlayType(null);
      }
      return;
    }

    const type = getOverlayTypeForCurrentTime(now, nextEvent);

    if (type !== overlayType) {
      setOverlayType(type);
      //console.log("🎬 [OverlayController] Overlay Type:", type);

      if (onOverlayChange) onOverlayChange(type);

      if (type === "countdown") {
        const sec = getSecondsUntil(now, nextEvent.time);
        setCountdownSeconds(sec);
        //console.log("⏳ [OverlayController] Countdown Seconds:", sec);
      }
    }
  }, [now, nextEvent, overlayType, onOverlayChange]);

  // 🧩 4. Countdown timer updater
  useEffect(() => {
    // Only run this effect if we are actually in countdown mode
    if (overlayType !== "countdown" || !nextEvent) return;

    console.log("⏱ Starting Countdown Interval...");

    const interval = setInterval(() => {
      // Recalculate 'now' inside the interval to ensure it's fresh
      const currentMoment = moment.tz(moment(), timezone || moment.tz.guess());
      const remaining = getSecondsUntil(currentMoment, nextEvent.time);
      
      setCountdownSeconds(remaining);
      
      // Optional: Debug log to see if it's ticking in console
      // console.log("Ticking:", remaining); 
    }, 1000);

    // Initial set so we don't wait 1 second for the first update
    setCountdownSeconds(getSecondsUntil(moment.tz(moment(), timezone || moment.tz.guess()), nextEvent.time));

    return () => clearInterval(interval);
  }, [overlayType, nextEvent, timezone]);

  // 🧩 5. Rendering the active overlay
  //console.log("🟢 [OverlayController] Rendering:", overlayType);

  switch (overlayType) {
    case "countdown":
      return (
        <CountdownOverlay
          nextEvent={nextEvent}
          remainingSeconds={countdownSeconds}
          timezone={timezone}
        />
      );
    case "iqamah":
      return <IqamahOverlay nextEvent={nextEvent} timezone={timezone} />;
    case "salah":
      return <SalahProgressOverlay nextEvent={nextEvent} timezone={timezone} />;
    default:
      return null;
  }
};

export default PrayerOverlayController;
